import { type Ref, ref, computed } from "vue";
import GraphHelper from "@/helpers/utilities/GraphHelper";
import UserExtension from "@/models/extensions/education/UserExtension";
import apiGraph from "@/helpers/api/apiGraph";
import apiSkobo from "@/helpers/api/apiSkobo";
import { useSectionsStore } from "@/stores/sections";
import { useSettingsStore } from "@/stores/settings";

interface RawApiPerson {
  userId: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
  title?: string | null;
  emailSchoolAddress?: string;
  extSysApiUserId?: string;
  roles?: string[];
  identifier?: string;
}

interface InstitutionPerson {
  name: { firstName?: string; lastName?: string; name?: string; title?: string | null };
  emailSchoolAddress?: string;
  extSysApiUserId?: string;
  userId: string;
  roles?: string[];
  identifier?: string;
}

interface BatchResponseEntry {
  id: string;
  status: number;
  headers?: Record<string, string>;
  body?: string;
}

interface PersonLike {
  extSysApiUserId?: string;
  emailSchoolAddress?: string;
  [key: string]: unknown;
}

export const useInstitutionComposable = () => {
  const usersProfilePictures: Ref<Record<string, string | null>> = ref({});
  const profilePictures: Ref<Record<string, string | null>> = ref({});
  const allOnInstitution: Ref<InstitutionPerson[]> = ref([]);

  const loadAllPeopleOnInstitution = async () => {
    if (allOnInstitution.value && allOnInstitution.value.length > 0) {
      return;
    }

    const studentsResponse = await apiSkobo.get("/Institution/Member/Search/Student");
    const teachersResponse = await apiSkobo.get("/Institution/Member/Search/Teacher");
    const allPeople: RawApiPerson[] = studentsResponse.data.concat(teachersResponse.data);

    const allPeopleDistinct: RawApiPerson[] = [];
    const idMap = new Map<string, boolean>();

    for (const item of allPeople) {
      if (!idMap.has(item.userId)) {
        idMap.set(item.userId, true);
        allPeopleDistinct.push(item);
      }
    }

    const userForStore: InstitutionPerson[] = allPeopleDistinct.map(p => ({
      name: {
        firstName: p.firstName,
        lastName: p.lastName,
        name: p.displayName,
        title: p.title
      },
      emailSchoolAddress: p.emailSchoolAddress,
      extSysApiUserId: p.extSysApiUserId,
      userId: p.userId,
      roles: p.roles,
      identifier: p.identifier
    }));
    const settingsStore = useSettingsStore();
    userForStore.sort((a, b) => (a.name.name || "").localeCompare(b.name.name || "", settingsStore.language));
    allOnInstitution.value = userForStore;
  };

  /**
   * Load profile pictures for a list of users
   */
  const loadProfilePicturesFromList = (userList: PersonLike[]) => {
    return new Promise<void>((resolve, reject) => {
      const requestForUsersNotInState = userList
        .filter(u => u.extSysApiUserId && !usersProfilePictures.value[u.extSysApiUserId])
        .map(u => {
          usersProfilePictures.value[u.extSysApiUserId!] = null;
          return UserExtension.generateGraphRequestFromUser(u);
        });

      if (requestForUsersNotInState.length === 0) {
        resolve();
        return;
      }

      GraphHelper.makeBatchRequest(requestForUsersNotInState)
        .then((results: BatchResponseEntry[]) => {
          results.map(u => {
            if (u.status === 200) {
              const url = "data:" + u.headers!["Content-Type"] + ";base64," + u.body;

              fetch(url).then(res =>
                res.blob().then(blob => {
                  const objectUrl = window.URL.createObjectURL(blob);
                  usersProfilePictures.value[u.id] = objectUrl;
                })
              );
            }
          });

          resolve();
        })
        .catch((error: unknown) => {
          userList.map(u => {
            delete (loadProfilePicture as unknown as Record<string, unknown>)[u.extSysApiUserId!];
          });
          reject((error as Error)?.message);
        });
    });
  };

  const loadProfilePicture = async (person: PersonLike) => {
    if (!person || (!person.extSysApiUserId && !person.emailSchoolAddress)) {
      return;
    }

    if (!person.extSysApiUserId) {
      let personFromState: InstitutionPerson | undefined;

      const sectionsStore = useSectionsStore();
      sectionsStore.peopleInMySections.find(
        (p: { emailSchoolAddress?: string }) => p.emailSchoolAddress === person.emailSchoolAddress
      );

      if (!personFromState) {
        await loadAllPeopleOnInstitution();
        personFromState = allOnInstitution.value.find(p => p.emailSchoolAddress === person.emailSchoolAddress);
      }

      if (personFromState) {
        person.extSysApiUserId = personFromState.extSysApiUserId;
      } else {
        person.extSysApiUserId = person.emailSchoolAddress;
      }
    }

    if (Object.hasOwn(usersProfilePictures.value, person.extSysApiUserId!)) {
      try {
        const response = await apiGraph.get(`/users/${person.extSysApiUserId}/photos/48x48/$value`, {
          responseType: "blob"
        });

        const url = window.URL.createObjectURL(new Blob([response.data]));
        const testImg = new Image();
        testImg.onload = () => {
          usersProfilePictures.value[person.extSysApiUserId!] = url;
        };
        testImg.onerror = () => {
          setTimeout(() => {
            /* TIL KATE
            Dispatch er Vuex sprog - her kalder du bare funktionen længere oppe med this.loadProfilePicture
            */
            //dispatch("loadProfilePicture", person);
          }, 3000);
        };
        testImg.src = url;
      } catch (e: unknown) {
        const err = e as { response?: { status?: number } };
        if (err.response && err.response.status === 401) {
          // If not authorized, try again in a few seconds
          setTimeout(() => {
            /* TIL KATE
            Dispatch er Vuex sprog - her kalder du bare funktionen længere oppe med this.loadProfilePicture
            */
            //dispatch("loadProfilePicture", person);
          }, 3000);
        } else {
          // user has no image
          usersProfilePictures.value[person.extSysApiUserId!] = null;
        }
      }
    }
  };

  interface VuexCommitContext {
    commit: (action: string, payload: unknown) => void;
  }

  interface GraphUser {
    id?: string;
    mail?: string;
    extSysApiUserId?: string;
    givenName?: string;
    surname?: string;
    displayName?: string;
  }

  const loadPersonFromName = async ({ commit }: VuexCommitContext, { name }: { name: string }) => {
    const response = await apiGraph.get(
      `https://graph.microsoft.com/v1.0/users?$count=true&$search="displayName:${name}"`,
      {
        headers: { ConsistencyLevel: "eventual" }
      }
    );

    response.data.value.forEach((v: GraphUser) => {
      if (!allOnInstitution.value.find(a => a.extSysApiUserId === v.extSysApiUserId)) {
        allOnInstitution.value.push(v as unknown as InstitutionPerson);
      }
      commit("loadPersonFromName", {
        emailSchoolAddress: v.mail,
        extSysApiUserId: v.id,
        name: {
          firstName: v.givenName,
          lastName: v.surname,
          name: v.displayName
        },
        roles: "student"
      });
    });
  };

  // Getters
  const pictures = computed(() => usersProfilePictures.value);

  const picture = computed(() => (identifier: string) => {
    if (!identifier) {
      return "";
    }

    if (usersProfilePictures.value && usersProfilePictures.value[identifier]) {
      return usersProfilePictures.value[identifier];
    }

    if (identifier.indexOf("@") > -1) {
      let person = allOnInstitution.value.find(p => p.emailSchoolAddress === identifier);

      if (!person) {
        person = allOnInstitution.value.find(p => p.emailSchoolAddress === person?.emailSchoolAddress);
      }

      if (person && person.extSysApiUserId) {
        return usersProfilePictures.value[person.extSysApiUserId];
      }
    } else {
      const person = allOnInstitution.value.find(p => p.extSysApiUserId === identifier);

      if (person && person.emailSchoolAddress) {
        return usersProfilePictures.value[person.emailSchoolAddress];
      }
    }

    return null;
  });

  const allStudents = computed(() => allOnInstitution.value.filter(p => (p.roles || []).indexOf("student") > -1));
  const allTeachers = computed(() => allOnInstitution.value.filter(p => (p.roles || []).indexOf("teacher") > -1));
  const all = computed(() => allOnInstitution.value);

  const fromId = computed(() => (userId: string) => allOnInstitution.value.find(p => p.userId === userId));

  const fromExternalApiUserId = computed(
    () => (extSysApiUserId: string) => allOnInstitution.value.find(p => p.extSysApiUserId === extSysApiUserId)
  );

  const fromEmail = computed(() => (email: string) => {
    return allOnInstitution.value.find(
      p => p.emailSchoolAddress && p.emailSchoolAddress.toLowerCase() === email.toLowerCase()
    );
  });

  const fromName = computed(
    () => (name: string) =>
      allOnInstitution.value.filter(p => p.name && p.name.name?.toLowerCase() === name.toLowerCase())
  );

  return {
    usersProfilePictures,
    profilePictures,
    allOnInstitution,
    loadProfilePicturesFromList,
    loadProfilePicture,
    loadAllPeopleOnInstitution,
    loadPersonFromName,
    pictures,
    picture,
    allStudents,
    allTeachers,
    all,
    fromId,
    fromExternalApiUserId,
    fromEmail,
    fromName
  };
};
